import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import {
  msgObj,
  type MsgObjCommandTarget,
  type MsgObjEditorState,
  type MsgObjMainState,
  type MsgObjSemanticCommand,
  type MsgObjState,
  type MsgObjTemplateState,
  type SubsSection,
} from '@/msgdeskRuntime/state'

export interface MsgAppReactSurface {
  renderer: 'react'
  surface: string
}

export interface MsgAppBus {
  emit: (eventName: string, ...args: any[]) => void
  on?: (eventName: string, handler: (...args: any[]) => void) => () => void
}

export interface MsgApp {
  readonly state: MsgObjState
  readonly subs: typeof msgObj.subs
  readonly main: { getState: () => MsgObjMainState }
  readonly editor: { getState: () => MsgObjEditorState }
  readonly template: { getState: () => MsgObjTemplateState; updateSlot: (slot: string, type: 'text' | 'val' | 'html', value: string) => void }
  readonly command: typeof msgObj.command
  readonly legacy: typeof msgObj.legacy
  readonly facade: typeof msgObj
  readonly react: MsgAppReactSurface
  readonly bus: MsgAppBus
  getState(): MsgObjState
  subscribe(listener: () => void): () => void
  send(command: MsgObjSemanticCommand): unknown
  setChecked(target: MsgObjCommandTarget, checked: boolean): unknown
  setActive(target: MsgObjCommandTarget, active: boolean): unknown
  setCurrentPage(section: SubsSection, page: number): unknown
}

function createMsgApp(surface: string): MsgApp {
  const runtime = getMsgDeskRuntime(surface)
  const app: MsgApp = {
    get state() {
      return msgObj.getState()
    },
    get subs() {
      return msgObj.subs
    },
    get main() {
      return msgObj.main
    },
    get editor() {
      return msgObj.editor
    },
    get template() {
      return msgObj.template
    },
    get command() {
      return msgObj.command
    },
    get legacy() {
      return msgObj.legacy
    },
    get facade() {
      return msgObj
    },
    get react() {
      return { renderer: 'react', surface } as const
    },
    get bus() {
      return runtime.eventBus
    },
    getState: () => msgObj.getState(),
    subscribe: (listener: () => void) => msgObj.subscribe(listener),
    send: (command: MsgObjSemanticCommand) => msgObj.command.send(command),
    setChecked: (target: MsgObjCommandTarget, checked: boolean) => msgObj.command.setChecked(target, checked),
    setActive: (target: MsgObjCommandTarget, active: boolean) => msgObj.command.setActive(target, active),
    setCurrentPage: (section: SubsSection, page: number) => msgObj.command.setCurrentPage(section, page),
  }

  return app
}

export function installMsgDeskAppBridge(surface = 'template2'): MsgApp {
  const runtime = getMsgDeskRuntime(surface)
  const existing = runtime.getInstalledApp<MsgApp>()
  if (existing) {
    if (!existing.react) {
      Object.defineProperty(existing, 'react', { value: { renderer: 'react', surface }, configurable: true })
    }
    return runtime.installCompatibilityApp(existing)
  }

  const app = createMsgApp(surface)
  return runtime.installCompatibilityApp(app)
}

export const msgApp = createMsgApp('template2')
